import {Alert, Center, Group, Loader} from '@mantine/core'
import {useMediaQuery} from '@mantine/hooks'
import {notifications} from '@mantine/notifications'
import {useQueryClient} from '@tanstack/react-query'
import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import {useCurrentUser} from '../auth/use-current-user'
import {useCustomers} from '../customers/use-customers'
import {
    filterDispatchTimelineDataByCustomer,
    filterDispatchTimelineDataByYard,
} from '../dispatch/customer-filter'
import {useDispatchBoardContext} from '../dispatch/dispatch-board-context'
import {useBoardJobChanges} from '../dispatch/use-board-job-changes'
import {useDispatchYards} from '../dispatch/use-dispatch'
import {copyDriverJobLink} from '../driver-view/copy-driver-job-link'
import {DriverLinkDialog} from '../driver-view/driver-link-dialog'
import type {DriverLink} from '../driver-view/schemas'
import {EquipmentExceptionsLink} from '../equipment-checks/equipment-exceptions-link'
import {isManager, publishableYards} from '../jobs/edit-rights'
import {stockEquipmentKeys} from '../jobs/stock-equipment/query-keys'
import {ActiveRentalsBoardView} from '../rentals/active-rentals-board-view'
import {alertToneColor, signalAttentionColor} from '../theme/semantic-colors'
import {PickupsToConfirmLink} from '../work/pickups-to-confirm-link'
import {computeAllocationStatus} from './allocation'
import {DispatchCustomerEmptyState} from './board-empty-state'
import {type BoardLayout, DEFAULT_BOARD_LAYOUT} from './board-layout'
import {CrossYardPanel} from './cross-yard-panel'
import {DispatchCardBoard} from './dispatch-card-board'
import {DispatchJobModal} from './dispatch-job-modal'
import {DispatchTimelineHeader} from './dispatch-timeline-header'
import {DriversPanel} from './drivers-panel'
import type {TimelineJobRow} from './job-rows'
import {timelineRowForBoardJobId, timelineRowFromUnassignedJob} from './job-rows'
import {PublishScheduleModal} from './publish-schedule-modal'
import {RunMapView} from './run-map/run-map-view'
import {stickyClasses} from './swim-lane-sticky'
import {TerminalBoard} from './terminal-board'
import {projectTimelineData} from './timeline-view'
import type {JobAllocation, JobRequirement, UnassignedJob, ViewRange} from './types'
import type {WidescreenBoardProps} from './widescreen-board-common'

type DispatchTimelinePageProps = {
    viewRange: ViewRange
    onViewRangeChange: (value: ViewRange) => void
    anchorOffset: number
    onAnchorOffsetChange: (value: number) => void
    customerId: string | null
    onCustomerIdChange?: (customerId: string | null) => void
    yardId?: string | null
    onYardChange?: (yardId: string | null) => void
}

/** Non-overlapping narrow range matching the app shell's `48em` mobile boundary. */
const MOBILE_VIEWPORT_QUERY = '(max-width: 47.9375em)'

export function DispatchTimelinePage({
    viewRange,
    onViewRangeChange,
    anchorOffset,
    onAnchorOffsetChange,
    customerId,
    onCustomerIdChange,
    yardId = null,
    onYardChange,
}: DispatchTimelinePageProps) {
    const {
        canonicalData,
        isLoading,
        error,
        allocateResources,
        replaceAllocations,
        applyStockDelta,
        notifyDriver,
        refresh,
    } = useDispatchBoardContext()
    const {data: customers = [], isLoading: customerOptionsLoading} = useCustomers()
    const {data: yards = []} = useDispatchYards()
    const currentUser = useCurrentUser()
    const yardsToPublish = publishableYards(currentUser, yards)
    const canPublish =
        yardId == null
            ? yardsToPublish.length > 0
            : yardsToPublish.some((yard) => yard.id === yardId)
    const [publishOpen, setPublishOpen] = useState(false)
    const [driverLinkToCopy, setDriverLinkToCopy] = useState<DriverLink | null>(null)
    const queryClient = useQueryClient()
    const [boardLayout, setBoardLayout] = useState<BoardLayout>(DEFAULT_BOARD_LAYOUT)
    const [mapView, setMapView] = useState(false)
    const [rentalsView, setRentalsView] = useState(false)
    const isMobileViewport = useMediaQuery(MOBILE_VIEWPORT_QUERY)
    // The stored layout is the desktop preference; mobile always renders Cards
    // so the preference survives crossing the breakpoint in either direction.
    const effectiveBoardLayout: BoardLayout = isMobileViewport ? 'cards' : boardLayout
    const [selectedRow, setSelectedRow] = useState<TimelineJobRow | null>(null)
    const [highlightAssignmentId, setHighlightAssignmentId] = useState<string | null>(null)
    const headerRef = useRef<HTMLDivElement>(null)
    const [stickyOffset, setStickyOffset] = useState(0)
    const customerOptions = useMemo(
        () => customers.map((customer) => ({value: customer.id, label: customer.name})),
        [customers],
    )
    const activeCustomerId = customerId
    const dispatchContext = `${activeCustomerId ?? ''}:${yardId ?? ''}:${viewRange}:${anchorOffset}`
    const previousDispatchContext = useRef(dispatchContext)
    const filteredCanonicalData = useMemo(
        () =>
            filterDispatchTimelineDataByCustomer(
                filterDispatchTimelineDataByYard(canonicalData, yardId),
                activeCustomerId,
            ),
        [activeCustomerId, canonicalData, yardId],
    )

    useLayoutEffect(() => {
        const node = headerRef.current
        if (!node) {
            return
        }

        const syncOffset = () => {
            setStickyOffset(node.getBoundingClientRect().height)
        }

        syncOffset()
        const observer = new ResizeObserver(syncOffset)
        observer.observe(node)
        return () => observer.disconnect()
    }, [])

    const displayData = useMemo(() => {
        return projectTimelineData(filteredCanonicalData, viewRange, anchorOffset)
    }, [filteredCanonicalData, viewRange, anchorOffset])
    const unfilteredDisplayData = useMemo(() => {
        return projectTimelineData(canonicalData, viewRange, anchorOffset)
    }, [canonicalData, viewRange, anchorOffset])
    const jobChanges = useBoardJobChanges({
        canonicalData,
        yards,
        refresh,
        closeJob: () => setSelectedRow(null),
    })

    useEffect(() => {
        if (previousDispatchContext.current === dispatchContext) {
            return
        }
        previousDispatchContext.current = dispatchContext
        setSelectedRow(null)
    }, [dispatchContext])

    // Clear the selection once the job is missing from the unfiltered board data.
    useEffect(() => {
        if (!selectedRow) {
            return
        }

        const selectedJobId = selectedRow.sourceJobId ?? selectedRow.id
        if (!timelineRowForBoardJobId(canonicalData, selectedJobId)) {
            setSelectedRow(null)
        }
    }, [canonicalData, selectedRow])

    useEffect(() => {
        if (!highlightAssignmentId) return

        const timer = window.setTimeout(() => setHighlightAssignmentId(null), 2400)
        return () => window.clearTimeout(timer)
    }, [highlightAssignmentId])

    const handleJobPress = (job: UnassignedJob) => {
        const canonicalJob =
            canonicalData.unassignedJobs.find((entry) => entry.id === job.id) ?? job
        setSelectedRow(timelineRowFromUnassignedJob(canonicalJob))
    }

    const handleCommittedPress = (row: TimelineJobRow) => {
        setSelectedRow(row)
    }

    const handleOpenBoardJob = (jobId: string) => {
        const row =
            timelineRowForBoardJobId(canonicalData, jobId) ??
            timelineRowForBoardJobId(unfilteredDisplayData, jobId)
        setSelectedRow(row)
    }

    const handleSubmitAllocations = async (pending: JobAllocation[]) => {
        const allocatingJob = selectedRow?.unassignedJob
            ? (canonicalData.unassignedJobs.find(
                  (entry) => entry.id === selectedRow.unassignedJob!.id,
              ) ?? selectedRow.unassignedJob)
            : null

        if (!allocatingJob || pending.length === 0) {
            return
        }

        try {
            const next = await allocateResources(
                allocatingJob.id,
                pending.map((allocation) => ({
                    requirementId: allocation.requirementId,
                    resourceId: allocation.resourceId,
                    resourceName: allocation.resourceName,
                    kind: allocation.kind,
                    ...(allocation.days ? {days: allocation.days} : {}),
                })),
            )

            const updatedJob = next.unassignedJobs.find((entry) => entry.id === allocatingJob.id)
            if (updatedJob) {
                setSelectedRow(timelineRowFromUnassignedJob(updatedJob))
            } else {
                setSelectedRow(null)
            }

            if (updatedJob && computeAllocationStatus(updatedJob) === 'ready') {
                notifications.show({
                    title: 'Crew complete',
                    message: `${allocatingJob.label} is fully assigned. Send each driver their job message when ready.`,
                    color: signalAttentionColor(),
                })
                return
            }

            notifications.show({
                title: 'Assignment saved',
                message: `${pending.length} resource${pending.length === 1 ? '' : 's'} assigned to ${allocatingJob.label}.`,
                color: alertToneColor('success'),
            })
        } catch (allocateError) {
            notifications.show({
                title: 'Not saved',
                message:
                    allocateError instanceof Error
                        ? allocateError.message
                        : 'Could not save allocation',
                color: alertToneColor('danger'),
            })
            throw allocateError
        }
    }

    const handleApplyStockDelta = async (requirement: JobRequirement, delta: number) => {
        const allocatingJob = selectedRow?.unassignedJob
            ? (canonicalData.unassignedJobs.find(
                  (entry) => entry.id === selectedRow.unassignedJob!.id,
              ) ?? selectedRow.unassignedJob)
            : null

        if (!allocatingJob || !requirement.rentalAssetTypeId || delta === 0) {
            return
        }

        try {
            const next = await applyStockDelta(allocatingJob.id, {
                rentalAssetTypeId: requirement.rentalAssetTypeId,
                delta,
            })

            const updatedJob = next.unassignedJobs.find((entry) => entry.id === allocatingJob.id)
            if (updatedJob) {
                setSelectedRow(timelineRowFromUnassignedJob(updatedJob))
            } else {
                setSelectedRow(null)
            }

            void queryClient.invalidateQueries({
                queryKey: stockEquipmentKeys.assignable(
                    allocatingJob.id,
                    requirement.rentalAssetTypeId,
                ),
            })

            notifications.show({
                title: 'Assignment saved',
                message: `${allocatingJob.label} stock assignment updated.`,
                color: alertToneColor('success'),
            })
        } catch (allocateError) {
            notifications.show({
                title: 'Not saved',
                message:
                    allocateError instanceof Error
                        ? allocateError.message
                        : 'Could not save allocation',
                color: alertToneColor('danger'),
            })
            throw allocateError
        }
    }

    const handleReplaceAllocations = async (nextAllocations: JobAllocation[]) => {
        const allocatingJob = selectedRow?.unassignedJob
            ? (canonicalData.unassignedJobs.find(
                  (entry) => entry.id === selectedRow.unassignedJob!.id,
              ) ?? selectedRow.unassignedJob)
            : null

        if (!allocatingJob) {
            return
        }

        try {
            const next = await replaceAllocations(allocatingJob.id, nextAllocations)
            const updatedJob = next.unassignedJobs.find((entry) => entry.id === allocatingJob.id)
            if (updatedJob) {
                setSelectedRow(timelineRowFromUnassignedJob(updatedJob))
            } else {
                setSelectedRow(null)
            }
            notifications.show({
                title: 'Assignments updated',
                message: `${allocatingJob.label} assignments were updated.`,
                color: alertToneColor('success'),
            })
        } catch (replaceError) {
            notifications.show({
                title: 'Update failed',
                message:
                    replaceError instanceof Error
                        ? replaceError.message
                        : 'Could not update assignments',
                color: alertToneColor('danger'),
            })
            throw replaceError
        }
    }

    const handleCopyDriverLink = async (workerId: string) => {
        const job = selectedRow?.unassignedJob
        if (job) {
            setDriverLinkToCopy(await copyDriverJobLink(job.id, workerId))
        }
    }

    const handleMarkDriverSent = async (resourceId: string) => {
        const notifyingJob = selectedRow?.unassignedJob
            ? (canonicalData.unassignedJobs.find(
                  (entry) => entry.id === selectedRow.unassignedJob!.id,
              ) ?? selectedRow.unassignedJob)
            : null

        if (!notifyingJob) {
            return
        }

        const driver = notifyingJob.allocations.find(
            (entry) => entry.kind === 'worker' && entry.resourceId === resourceId,
        )

        try {
            const next = await notifyDriver(notifyingJob.id, resourceId)
            const updatedJob = next.unassignedJobs.find((entry) => entry.id === notifyingJob.id)
            const committedAssignment = next.assignments.find(
                (entry) => entry.sourceJobId === notifyingJob.id,
            )

            if (committedAssignment) {
                setSelectedRow(null)
                window.requestAnimationFrame(() => {
                    setHighlightAssignmentId(committedAssignment.id)
                })
                notifications.show({
                    title: 'Dispatched',
                    message: `${notifyingJob.label} is fully allocated and all drivers are marked notified.`,
                    color: alertToneColor('success'),
                })
                return
            }

            if (updatedJob) {
                setSelectedRow(timelineRowFromUnassignedJob(updatedJob))
            } else {
                setSelectedRow(null)
            }

            notifications.show({
                title: 'Marked notified',
                message: driver
                    ? `${driver.resourceName} marked notified for ${notifyingJob.label}.`
                    : `Driver marked notified for ${notifyingJob.label}.`,
                color: alertToneColor('success'),
            })
        } catch (notifyError) {
            notifications.show({
                title: 'Could not mark notified',
                message:
                    notifyError instanceof Error
                        ? notifyError.message
                        : 'Could not mark driver notified',
                color: alertToneColor('danger'),
            })
            throw notifyError
        }
    }

    let board: ReactNode = null
    let status: ReactNode = null

    if (isLoading) {
        status = (
            <Center py="xl">
                <Loader aria-label="Loading dispatch board" />
            </Center>
        )
    } else if (error) {
        status = (
            <Alert color={alertToneColor('danger')} title="Could not load dispatch board">
                {error}
            </Alert>
        )
    } else if (rentalsView) {
        board = <ActiveRentalsBoardView yardId={yardId} />
    } else if (mapView) {
        board = (
            <RunMapView
                data={canonicalData}
                yardId={yardId}
                customerId={activeCustomerId}
                onOpenJob={handleOpenBoardJob}
            />
        )
    } else {
        const hasFilteredJobs =
            filteredCanonicalData.unassignedJobs.length > 0 ||
            filteredCanonicalData.assignments.length > 0

        if (activeCustomerId && !hasFilteredJobs) {
            board = <DispatchCustomerEmptyState />
        } else {
            const boardProps: WidescreenBoardProps = {
                data: displayData,
                viewRange,
                onJobPress: handleJobPress,
                onCommittedPress: handleCommittedPress,
                highlightAssignmentId,
            }
            board = {
                list: <TerminalBoard {...boardProps} />,
                cards: <DispatchCardBoard {...boardProps} />,
            }[effectiveBoardLayout]
        }
    }

    return (
        <div
            className={stickyClasses['stickyScope']}
            style={{'--dispatch-sticky-offset': `${stickyOffset}px`} as CSSProperties}
        >
            <DispatchTimelineHeader
                ref={headerRef}
                viewRange={viewRange}
                onViewRangeChange={onViewRangeChange}
                anchorOffset={anchorOffset}
                onAnchorOffsetChange={onAnchorOffsetChange}
                boardLayout={effectiveBoardLayout}
                onBoardLayoutChange={setBoardLayout}
                isMobileViewport={isMobileViewport}
                customerId={customerId}
                customerOptions={customerOptions}
                customerOptionsLoading={customerOptionsLoading}
                onCustomerIdChange={onCustomerIdChange}
                yards={yards}
                yardId={yardId}
                onYardChange={onYardChange}
                onPublish={canPublish ? () => setPublishOpen(true) : undefined}
                mapView={mapView}
                onMapViewChange={setMapView}
                rentalsView={rentalsView}
                onRentalsViewChange={setRentalsView}
            />
            <Group gap="lg" align="flex-start">
                <EquipmentExceptionsLink yardId={yardId} />
                <PickupsToConfirmLink yardId={yardId} onConfirmed={() => void refresh()} />
            </Group>
            {yards.length > 0 ? (
                <DriversPanel
                    onChanged={refresh}
                    yardId={yardId}
                    yardName={yards.find((yard) => yard.id === yardId)?.name ?? null}
                />
            ) : null}
            {!isLoading && !error && !rentalsView ? (
                <CrossYardPanel
                    data={canonicalData}
                    yardId={yardId}
                    yards={yards}
                    onOpenJob={handleOpenBoardJob}
                />
            ) : null}
            <DriverLinkDialog link={driverLinkToCopy} onClose={() => setDriverLinkToCopy(null)} />
            <PublishScheduleModal
                opened={publishOpen}
                onClose={() => setPublishOpen(false)}
                yards={yardsToPublish}
                yardId={yardId}
                defaultYardId={currentUser?.dispatchYardId ?? null}
                canSendSummary={currentUser != null && isManager(currentUser)}
            />
            {status}
            {board}
            <DispatchJobModal
                opened={selectedRow !== null}
                row={selectedRow}
                canonicalData={canonicalData}
                displayData={unfilteredDisplayData}
                viewRange={viewRange}
                onClose={() => setSelectedRow(null)}
                onSubmit={handleSubmitAllocations}
                onReplaceAllocations={handleReplaceAllocations}
                onApplyStockDelta={handleApplyStockDelta}
                onCopyDriverLink={handleCopyDriverLink}
                onMarkDriverSent={handleMarkDriverSent}
                onOpenBoardJob={handleOpenBoardJob}
                onExtendEndDate={jobChanges.extendEndDate}
                onMarkCompleted={jobChanges.markCompleted}
                yards={yards}
                onSetProjectYard={jobChanges.setProjectYard}
                onSetJobDate={jobChanges.setJobDate}
                onDidNotHappen={jobChanges.reportDidNotHappen}
                onPickupConfirmed={() => void refresh()}
                onRentalsConfirmed={() => void refresh()}
                onPickupCompleted={async () => {
                    setSelectedRow(null)
                    await refresh()
                }}
            />
        </div>
    )
}
